import { ChildModal, Modal, ToastContext } from 'cat-ecommerce-alloy';
import { ModalVariant } from 'cat-ecommerce-alloy/dist/components/alloy-modal-v2/Modal.types';
import SOSModal from '../SOSModal';
import AlternativePartsModal from '../AlternativePartsModal';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useBreakpoint, usePrevious } from '@app/hooks';
import ReplacementPartsModal from '../ReplacementPartsModal';
import {
  ADD_TO_CART_RESET_PHASE_AND_FLAGS,
  CHECKOUT_CART_FORM_SUBMIT_BEGIN,
  CHECKOUT_CART_FORM_SUBMIT_SUCCESS
} from '@app/store/checkout/constants';
import { PAGE_BREAKPOINTS } from '@app/constants/commonConstants';
import { useValidateQuantity } from '../hooks';
import useGACartEvents from '../../ShoppingCartPage/hooks/useGACartEvents';
import {
  ADD_TO_CART_PHASES,
  FORMIK_INITAL_VALUES
} from '../../ShoppingCartPage/constants';
import {
  addPartsToCart,
  getDealerPriceAndAvailability,
  getDealerSOSDetails,
  updateDateAndEquipment
} from '@app/store/checkout/actions';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';
import {
  fetchPriceAvailability,
  proceedToCheckout
} from '@app/store/orders/actions';
import { debounce, getCookie, replaceTokensInString } from '@app/utils';
import { removeQuickOrderCookie, setSOSPartsErrors } from '../utils';
import {
  ERROR_DOMAIN,
  ERROR_PATH,
  UNKNOWNERROR,
  UNKNOWN_ERROR_CANCEL_IMPORT
} from '@app/constants/errorConstants';
import { clearError } from '@app/store/exception/actions';

const AddToCartModifications = ({
  modalOptions,
  isAvailabilityToggled,
  setIsAvailabilityToggled,
  isModalTypeSis
}) => {
  const [t] = useTranslation();
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const dispatch = useDispatch();
  const { values, setErrors, setFormikState, setFieldTouched, validateForm } =
    useFormikContext();
  const toastCtx = useContext(ToastContext);
  const { fireGAEventFunction } = useGACartEvents();

  const storeId = useSelector(state => state.common.storeId);
  const current = useSelector(s => s.orders.current);
  const hasSOS = useSelector(s => s.checkout?.currentAddToCartHasSOS);
  const isFromQuickOrder = useSelector(s => s.checkout?.isFromQuickOrder);
  const hasAlternatives = useSelector(
    s => s.checkout.currentAddToCartHasAlternate
  );
  const hasReplacements = useSelector(
    s => s.checkout.currentAddToCartHasReplacement
  );
  const orderItems = useSelector(
    s => s?.orders?.byId[current]?.orderItem || []
  );
  const sosModalTitle = t('SOURCE_OF_SUPPLY');
  const replacementsModalTitle = t('QUICK_ORDER_REPLACEMENT_PARTS');
  const alternateModalTitle = t('ALTERNATIVE_PARTS');
  const replacedParts = useSelector(s => s.orders.replacedParts);
  const replacementHasReplacement = !!replacedParts?.length;

  const phase = useSelector(s => s.checkout.currentAddToCartPhase);
  const previousPhase = usePrevious(phase);
  const hasPhaseChanged = previousPhase !== phase;

  const isAddToCartAndCheckoutFlow = useSelector(
    s => s.checkout.isCurrentAddToCartAndCheckoutFlow
  );

  const isPCC_SpecialCharactersValidationFlag = useSelector(
    s => s.featureFlag?.PCC_SpecialCharactersValidationFlag
  );

  const modalTitle = isModalTypeSis ? t('FULL_SIS_LABEL') : t('Quick_Title');

  const backButtonMessage = replaceTokensInString(
    t('GENERAL_BACK_TO'),
    modalTitle
  );
  /**
   * Using useRef(values) and modalOptionsRef to keep values, modalOptions, and orderItems out of the dependency array for callbacks and phase changes to
   * Quick Order Add To Cart Flow to avoid triggering this useeffect on every quick order line item changes or modal open or close
   */
  const valuesRef = useRef(values);
  const modalOptionsRef = useRef(modalOptions);
  const orderItemsRef = useRef(orderItems);
  useEffect(() => {
    valuesRef.current = values;
    modalOptionsRef.current = modalOptions;
    orderItemsRef.current = orderItems;
  }, [modalOptions, orderItems, values]);

  const handleChildModalClose = () => {
    dispatch(clearError(ERROR_DOMAIN.QUICK_ORDER, ERROR_PATH.ADD_TO_CART));
    modalOptions.close();
  };

  const { validateItemQuantity } = useValidateQuantity();
  const getErrorDomainPath = useCallback(
    () =>
      modalOptionsRef.current.isOpen
        ? {
            domain: ERROR_DOMAIN.QUICK_ORDER,
            path: ERROR_PATH.ADD_TO_CART,
            modalError: {
              message: UNKNOWN_ERROR_CANCEL_IMPORT,
              title: UNKNOWNERROR.title
            }
          }
        : {},
    [modalOptionsRef]
  );

  const updateFormikState = useCallback(() => {
    setFormikState(s => ({
      ...s,
      values: { ...valuesRef.current, items: FORMIK_INITAL_VALUES.items },
      errors: {},
      touched: {}
    }));
  }, [setFormikState]);

  const gaCallback = useCallback(
    quantityUpdatedFilteredItems => {
      fireGAEventFunction(quantityUpdatedFilteredItems, false, true);
    },
    [fireGAEventFunction]
  );

  const refreshCartCallback = useCallback(
    itemsAndQuantityAddedIntoCartObject => {
      modalOptionsRef.current.close();
      dispatch({ type: ADD_TO_CART_RESET_PHASE_AND_FLAGS });
      // clear quick order
      isFromQuickOrder && updateFormikState();
      isFromQuickOrder && removeQuickOrderCookie();
      dispatch(
        fetchPriceAvailability({
          errorInfo: getErrorDomainPath(),
          checkoutCallback: () => {},
          gaCallback,
          itemsAndQuantityAddedIntoCartObject
        })
      );
    },
    [
      dispatch,
      isFromQuickOrder,
      getErrorDomainPath,
      updateFormikState,
      gaCallback
    ]
  );

  const sendToCheckoutCallback = useCallback(
    async (_, orderData) => {
      modalOptionsRef.current.close();
      dispatch({ type: ADD_TO_CART_RESET_PHASE_AND_FLAGS });
      dispatch(clearError(ERROR_DOMAIN.CART, ERROR_PATH.SHOPPING_CART));
      dispatch({ type: CHECKOUT_CART_FORM_SUBMIT_BEGIN });
      // Need to set field touched here because these fields are outside
      // of this form and the error is not set for these fields
      setFieldTouched('requestByDate', true);
      setFieldTouched('equipment', true);
      const formErrors = await validateForm();
      const noFormErrors = Object.keys(formErrors).length === 0;
      const hasItemErrors = orderItemsRef.current.some(
        item => item.packageError
      );
      if (noFormErrors && !hasItemErrors) {
        // clear quick order form and cookie
        updateFormikState();
        removeQuickOrderCookie();
        dispatch(
          fetchPriceAvailability({
            errorInfo: getErrorDomainPath(),
            checkoutCallback: async () => {
              let updateDealerSession;
              const selectedStoreId = getCookie('selESiteId');
              if (selectedStoreId !== null && selectedStoreId !== undefined) {
                if (storeId !== selectedStoreId) {
                  updateDealerSession = 'Y';
                }
              }
              await dispatch(updateDateAndEquipment(valuesRef.current));
              const orderDetails = {
                orderId: orderData.orderId,
                updateDealerSession: updateDealerSession
                  ? updateDealerSession
                  : 'N'
              };
              dispatch(proceedToCheckout(orderDetails)).then(checkoutType => {
                if (checkoutType === 'emailCart') {
                  toastCtx.destroyAllToasts();
                  toastCtx.createWarningToast(t('NO_VALID_PAYMENT_EMAIL_CART'));
                }
              });
            }
          })
        );
      } else if (hasItemErrors) {
        dispatch(fetchPriceAvailability({ errorInfo: getErrorDomainPath() }));
      }
      dispatch({ type: CHECKOUT_CART_FORM_SUBMIT_SUCCESS });
    },
    [
      dispatch,
      setFieldTouched,
      validateForm,
      updateFormikState,
      getErrorDomainPath,
      storeId,
      toastCtx,
      t
    ]
  );

  /**
   * Dispatches calls for Quick Order Add to Cart flow to avoid having to hand
   * down callbacks through several actions and allows us to decouple the actions.
   * Additionally opens modal based on current phase in quick order.
   *
   * @param phase - current phase in add to cart flow
   *
   * This useeffect should trigger on phase changes only
   *
   * CALLING_SOS_SERVICE:
   *  for initial call to sos service by add to cart OR add to cart and checkout button
   * - Note: the 3 below phases could be one phase but are seperated for easier debugging and flexibility
   * SOS_MODAL:
   *  to open sos modal as parent after sos service succeeds and has multiple sos parts
   * REPLACEMENT_MODAL:
   *  to open replacement modal as parent after price and availability service succeeds and has replacement parts
   * ALTERNATE_MODAL:
   *  to open alternate modal as parent after price and availability service succeeds and has alternate parts
   * ADD_PARTS_TO_CART:
   *  to call addItems service either for add to cart OR add to cart and checkout
   *
   */
  const debouncedAddPartsToCart = useCallback(
    debounce(
      () =>
        dispatch(
          addPartsToCart(
            refreshCartCallback,
            setErrors,
            validateItemQuantity,
            t,
            getErrorDomainPath()
          )
        ),
      500,
      {
        leading: true,
        trailing: false
      }
    ),
    [addPartsToCart, dispatch, refreshCartCallback]
  );

  useEffect(() => {
    if (!hasPhaseChanged) {
      return;
    }
    const handlePartErrors = errors => {
      setSOSPartsErrors(
        errors,
        valuesRef.current,
        setErrors,
        t,
        isPCC_SpecialCharactersValidationFlag
      );
    };
    switch (phase) {
      case ADD_TO_CART_PHASES.CALLING_SOS_SERVICE:
        dispatch(
          getDealerSOSDetails({
            items: valuesRef.current?.items,
            invalidPartsHandler: handlePartErrors,
            t
          })
        );
        break;
      case ADD_TO_CART_PHASES.SOS_MODAL:
      case ADD_TO_CART_PHASES.REPLACEMENT_MODAL:
      case ADD_TO_CART_PHASES.ALTERNATE_MODAL:
        if (!modalOptionsRef.current.isOpen) {
          modalOptionsRef.current.open();
        }
        break;
      case ADD_TO_CART_PHASES.CALLING_PA_SERVICE:
        dispatch(getDealerPriceAndAvailability(getErrorDomainPath()));
        break;
      case ADD_TO_CART_PHASES.ADD_PARTS_TO_CART:
        if (isAddToCartAndCheckoutFlow) {
          dispatch(
            addPartsToCart(
              sendToCheckoutCallback,
              setErrors,
              validateItemQuantity,
              t,
              getErrorDomainPath()
            )
          );
        } else {
          debouncedAddPartsToCart();
        }
        break;
      default:
        break;
    }
  }, [
    dispatch,
    getErrorDomainPath,
    isAddToCartAndCheckoutFlow,
    phase,
    validateItemQuantity,
    refreshCartCallback,
    sendToCheckoutCallback,
    setErrors,
    t,
    hasPhaseChanged,
    isPCC_SpecialCharactersValidationFlag
  ]);

  if (!modalOptions?.isOpen) {
    return null;
  } else if (hasSOS && isMD) {
    return (
      <Modal
        contentLabel={sosModalTitle}
        contentClassName="pb-0"
        isOpen={modalOptions.isOpen}
        title={sosModalTitle}
        variant={ModalVariant.large}
      >
        <SOSModal
          modalOptions={modalOptions}
          isAvailabilityToggled={isAvailabilityToggled}
          setIsAvailabilityToggled={setIsAvailabilityToggled}
        />
      </Modal>
    );
  } else if (hasSOS && !isMD) {
    return (
      <ChildModal
        backButtonMessage={backButtonMessage}
        contentLabel={sosModalTitle}
        contentClassName="pb-0"
        isOpen={modalOptions.isOpen}
        close={handleChildModalClose}
        title={sosModalTitle}
        variant={ModalVariant.large}
      >
        <SOSModal
          modalOptions={modalOptions}
          isAvailabilityToggled={isAvailabilityToggled}
          setIsAvailabilityToggled={setIsAvailabilityToggled}
        />
      </ChildModal>
    );
  } else if ((hasReplacements || replacementHasReplacement) && isMD) {
    return (
      <Modal
        contentLabel={replacementsModalTitle}
        contentClassName="pb-0"
        isOpen={modalOptions.isOpen}
        title={replacementsModalTitle}
        variant={ModalVariant.large}
      >
        <ReplacementPartsModal
          modalOptions={modalOptions}
          isAvailabilityToggled={isAvailabilityToggled}
          setIsAvailabilityToggled={setIsAvailabilityToggled}
        />
      </Modal>
    );
  } else if ((hasReplacements || replacementHasReplacement) && !isMD) {
    return (
      <ChildModal
        backButtonMessage={backButtonMessage}
        contentLabel={replacementsModalTitle}
        contentClassName="pb-0"
        isOpen={modalOptions.isOpen}
        close={handleChildModalClose}
        title={replacementsModalTitle}
        variant={ModalVariant.large}
      >
        <ReplacementPartsModal
          modalOptions={modalOptions}
          isAvailabilityToggled={isAvailabilityToggled}
          setIsAvailabilityToggled={setIsAvailabilityToggled}
        />
      </ChildModal>
    );
  } else if (hasAlternatives && isMD) {
    return (
      <Modal
        contentLabel={alternateModalTitle}
        contentClassName="pb-0"
        isOpen={modalOptions.isOpen}
        title={alternateModalTitle}
        variant={ModalVariant.large}
      >
        <AlternativePartsModal
          modalOptions={modalOptions}
          isAvailabilityToggled={isAvailabilityToggled}
          setIsAvailabilityToggled={setIsAvailabilityToggled}
        />
      </Modal>
    );
  } else if (hasAlternatives && !isMD) {
    return (
      <ChildModal
        backButtonMessage={backButtonMessage}
        contentLabel={alternateModalTitle}
        contentClassName="pb-0"
        isOpen={modalOptions.isOpen}
        close={handleChildModalClose}
        title={alternateModalTitle}
        variant={ModalVariant.large}
      >
        <AlternativePartsModal
          modalOptions={modalOptions}
          isAvailabilityToggled={isAvailabilityToggled}
          setIsAvailabilityToggled={setIsAvailabilityToggled}
        />
      </ChildModal>
    );
  } else {
    return null;
  }
};

AddToCartModifications.propTypes = {
  modalOptions: PropTypes.shape({
    isOpen: PropTypes.bool,
    open: PropTypes.func,
    close: PropTypes.func
  }),
  quickOrderModalOptions: PropTypes.shape({
    isOpen: PropTypes.bool,
    open: PropTypes.func,
    close: PropTypes.func
  }),
  isAvailabilityToggled: PropTypes.bool,
  setIsAvailabilityToggled: PropTypes.func,
  isModalTypeSis: PropTypes.bool
};
export default AddToCartModifications;
